import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { customerClient } from "../api";
import { useI18n } from "../i18n";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { ChevronRight, ChevronLeft, ShoppingBag } from "lucide-react";

export default function MyOrders() {
  const { t } = useI18n();
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await customerClient.get("/orders/customer/mine");
        setOrders(data);
      } catch (e) {
        setErr(e?.response?.data?.detail ? String(e.response.data.detail) : "auth_required");
        setOrders([]);
      }
    })();
  }, []);

  const lastSlug = typeof window !== "undefined" ? (localStorage.getItem("foodgo_last_slug") || "") : "";
  const fallbackSlug = orders && orders[0] ? orders[0].restaurant_slug : lastSlug;

  return (
    <div className="min-h-screen bg-deep flex flex-col">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10 w-full flex-1" data-testid="my-orders-page">
        <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="chip chip-cyan inline-flex">{t.myOrders.tag}</div>
            <h1 className="font-heading text-4xl tracking-tight mt-3">{t.myOrders.title}</h1>
            <p className="text-zinc-400 text-sm mt-1">{t.myOrders.sub}</p>
          </div>
          {fallbackSlug && (
            <Link to={`/r/${fallbackSlug}`} className="btn-outline text-xs" data-testid="back-to-restaurant-btn">
              <ChevronLeft className="w-3.5 h-3.5" /> {t.myOrders.backToRestaurant || "Torna al ristorante"}
            </Link>
          )}
        </div>

        {orders === null && <div className="text-zinc-500 text-sm">…</div>}

        {orders && orders.length === 0 && (
          <div className="card-neon p-10 text-center" data-testid="my-orders-empty">
            <ShoppingBag className="w-10 h-10 text-zinc-600 mx-auto" />
            <p className="text-zinc-400 mt-4">{err === "auth_required" ? t.myOrders.loginPrompt : t.myOrders.empty}</p>
          </div>
        )}

        {orders && orders.length > 0 && (
          <div className="space-y-3" data-testid="my-orders-list">
            {orders.map((o) => {
              const pa = Number(o.points_awarded || 0);
              const pt = Number(o.points_balance_at_restaurant || 0);
              const itemsCount = (o.items || []).reduce((s, it) => s + (it.qty || 0), 0);
              return (
                <Link key={o.id} to={`/my-order/${o.id}`} className="card-neon p-5 hover:glow-cyan transition block" data-testid={`my-order-row-${o.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-neon-cyan font-mono">#{o.id.slice(-6)}</div>
                        <span className="text-[10px] font-mono uppercase text-zinc-400 chip !py-0 !px-1.5">{t.dashboard.mark[o.status] || o.status}</span>
                        {o.payment_method === "cash" && <span className="chip chip-yellow !text-[9px] !py-0 !px-1.5" data-testid={`row-cash-${o.id}`}>{t.myOrders.cashPay || "Cash"}</span>}
                      </div>
                      <div className="font-heading font-semibold mt-1 truncate">{o.restaurant_name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 font-mono">{new Date(o.created_at).toLocaleString()} · {itemsCount} {itemsCount === 1 ? (t.myOrders.itemSingular || "piatto") : (t.myOrders.itemPlural || "piatti")}</div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-heading font-bold text-neon-yellow text-xl">€ {Number(o.total).toFixed(2)}</div>
                      <ChevronRight className="w-5 h-5 text-zinc-500 inline-block mt-1" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-line flex items-center justify-between gap-3 text-xs font-mono">
                    <span className="text-zinc-400" data-testid={`row-points-awarded-${o.id}`}>
                      ⭐ {(t.myOrders.pointsForOrder || "Punti per questo ordine")}: <span className="text-neon-cyan font-bold">{pa}</span>
                    </span>
                    <span className="text-zinc-400" data-testid={`row-points-total-${o.id}`}>
                      {(t.myOrders.pointsTotal || "Totale punti")}: <span className="text-neon-yellow font-bold">{pt}</span>
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
